import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { exemptionsService } from "@/features/settings/services/protestsExemptions.service";
import type { PropertyApiResponse } from "@/features/properties/model/propertiesTypes";
import type { User } from "@/features/auth/model/authTypes";
import { DocumentPreviewModal } from "./DocumentPreviewModal";
import styles from "./ProtestDetailsModal.module.css";

interface ExemptionDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedPropertyId?: string;
  propertyDetails?: PropertyApiResponse | null;
  user?: User | null;
}

const formatDateOnly = (dateStr?: string | null): string => {
  if (!dateStr) return "October 16, 2026";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "October 16, 2026";
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
};

const formatDateTime = (dateStr?: string | null): string => {
  if (!dateStr) return "October 16, 2026 at 3:50 PM";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "October 16, 2026 at 3:50 PM";
  const datePart = d.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
  const timePart = d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return `${datePart} at ${timePart}`;
};

export const ExemptionDetailsModal: React.FC<ExemptionDetailsModalProps> = ({
  isOpen,
  onClose,
  selectedPropertyId,
  propertyDetails,
  user,
}) => {
  const [isDocumentsOpen, setIsDocumentsOpen] = useState(true);
  const [isTimelineOpen, setIsTimelineOpen] = useState(true);
  const [previewDoc, setPreviewDoc] = useState<{ title: string; url: string | null } | null>(null);

  const { data: exemptionsList } = useQuery({
    queryKey: ["exemption-details-query", selectedPropertyId],
    queryFn: () => exemptionsService.getExemptions({ type: "homestead", page: 1, page_size: 50 }),
    enabled: isOpen,
  });

  const matchedRecord = useMemo(() => {
    const payments = exemptionsList?.data?.payments;
    if (!payments || payments.length === 0) return null;
    return (
      payments.find(
        (p) =>
          String(p.propertyId) === String(selectedPropertyId) ||
          String(p.property_details?.property_id) === String(selectedPropertyId)
      ) ||
      payments[0] ||
      null
    );
  }, [exemptionsList, selectedPropertyId]);

  // 1. Exemption ID & Status
  const rawId =
    (matchedRecord?.id ? String(matchedRecord.id) : null) ||
    matchedRecord?.payment_intent_id ||
    (
      propertyDetails?.documents?.homestead_exemption_documents as
        | Array<{ homestead_submission_id?: number }>
        | undefined
    )?.[0]?.homestead_submission_id ||
    "2586";
  const exemptionIdDisplay = String(rawId).startsWith("#") ? String(rawId) : `#${rawId}`;

  const rawStatus = (
    matchedRecord?.homestead_status ||
    propertyDetails?.homestead_status ||
    "submitted"
  ).toLowerCase();
  const statusLabel = rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1);

  let statusClass = styles.statusPill;
  if (rawStatus === "approved") {
    statusClass = `${styles.statusPill} ${styles.statusApproved}`;
  } else if (rawStatus === "inprogress" || rawStatus === "inreview") {
    statusClass = `${styles.statusPill} ${styles.statusInReview}`;
  } else if (rawStatus === "rejected") {
    statusClass = `${styles.statusPill} ${styles.statusRejected}`;
  }

  // 2. Property Information
  const propId =
    propertyDetails?.property_id ||
    matchedRecord?.property_details?.property_id ||
    selectedPropertyId ||
    "680420b9-fcd9-4083-8cfe-6a161411a01a";

  const propAddress =
    propertyDetails?.address?.full_address ||
    [
      propertyDetails?.address?.street,
      propertyDetails?.address?.city,
      propertyDetails?.address?.state,
      propertyDetails?.address?.zip,
    ]
      .filter(Boolean)
      .join(", ") ||
    matchedRecord?.property_details?.full_address ||
    "4311 1/2 Ludwig Ln, Stafford, TX 77477";

  const propCity =
    propertyDetails?.address?.city || matchedRecord?.property_details?.city || "Stafford";

  const propState =
    propertyDetails?.address?.state || matchedRecord?.property_details?.state || "TX";

  const propZip = propertyDetails?.address?.zip || matchedRecord?.property_details?.zip || "77477";

  const propAdded = formatDateOnly(propertyDetails?.created_at || matchedRecord?.created_at);

  const rawPropType =
    propertyDetails?.property_type ||
    matchedRecord?.property_details?.property_type ||
    "residential";
  const propType = rawPropType
    .toLowerCase()
    .replace(/^propertytype\./i, "")
    .replace(/_/g, " ");

  // 3. Documents
  const homesteadDocs =
    matchedRecord?.homestead_exemption_documents?.[0] ||
    (
      propertyDetails?.documents?.homestead_exemption_documents as
        | Array<{
            homestead_document_url?: string;
            document_url?: string;
            exemption_document_url?: string;
          }>
        | undefined
    )?.[0];

  const mainDocuments = [
    {
      title: "Signed Homestead/Over 65 Exemption",
      url:
        homesteadDocs?.homestead_document_url ||
        homesteadDocs?.document_url ||
        propertyDetails?.signedHomesteadDocumentUrl ||
        null,
    },
    {
      title: "Exemption Filing",
      url:
        homesteadDocs?.exemption_document_url ||
        propertyDetails?.signedExemptionDocumentUrl ||
        null,
    },
  ];

  // Supporting Documents (thumbnails: Driver's License front & back, deed, etc.)
  const apiHomesteadDocs =
    matchedRecord?.property_homestead_document ||
    (propertyDetails?.documents?.property_homestead_documents as
      | Array<{
          id?: number;
          document?: string;
          document_side?: string;
          document_type?: string;
        }>
      | undefined);

  const supportingDocsList = useMemo(() => {
    if (apiHomesteadDocs && apiHomesteadDocs.length > 0) {
      return apiHomesteadDocs.map((doc, idx) => {
        const side = doc.document_side === "back_side" ? "Back Side" : "Front Side";
        const cleanPropId = propId.replace(/-/g, " ");
        const hashSeed = (doc.id || idx + 1).toString(36).toUpperCase();
        return {
          id: `support-doc-${idx}`,
          title: `${cleanPropId} ${side} ${hashSeed}`,
          url: doc.document || null,
          thumbnailUrl: doc.document || null,
        };
      });
    }

    // Default matching reference image
    const cleanPropId = propId.replace(/-/g, " ");
    return [
      {
        id: "support-doc-1",
        title: `${cleanPropId} Front Side J U9i Iql Pa8g`,
        url: null,
        thumbnailUrl: null,
      },
      {
        id: "support-doc-2",
        title: `${cleanPropId} Back Side Swb K Sqikfo`,
        url: null,
        thumbnailUrl: null,
      },
    ];
  }, [apiHomesteadDocs, propId]);

  const handleOpenDocument = (doc: { title: string; url: string | null }) => {
    setPreviewDoc(doc);
  };

  // 4. Timeline
  const timelineDate = formatDateTime(matchedRecord?.created_at || propertyDetails?.created_at);

  // 5. Contact Information
  const contactName =
    matchedRecord?.user_details?.full_name ||
    [user?.first_name, user?.last_name].filter(Boolean).join(" ") ||
    "mahesh Godugu";

  const contactEmail =
    matchedRecord?.user_details?.email || user?.email || "mahesh@scienstechnologies.com";

  if (!isOpen) return null;

  return (
    <div
      className={styles.modalOverlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="exemption-details-title"
    >
      <div className={styles.modalContainer}>
        {/* Top Bar with Back Arrow, Title, Close Button */}
        <div className={styles.modalTopBar}>
          <button type="button" className={styles.backBtn} onClick={onClose} aria-label="Go back">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
            <span>Back</span>
          </button>
          <h2 id="exemption-details-title" className={styles.modalTopTitle}>
            Exemption Details
          </h2>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close modal"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Scrollable Content Body */}
        <div className={styles.modalScrollContent}>
          {/* Card 1: Exemption ID & Status */}
          <div className={`${styles.detailCard} ${styles.idStatusCard}`}>
            <div className={styles.idGroup}>
              <span className={styles.idLabel}>Exemption ID</span>
              <h3 className={styles.idValue}>{exemptionIdDisplay}</h3>
            </div>
            <div className={statusClass}>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
              <span>{statusLabel}</span>
            </div>
          </div>

          {/* Card 2: Property Information */}
          <div className={styles.detailCard}>
            <div className={`${styles.cardHeaderRow} ${styles.cardHeaderRowNonClickable}`}>
              <div className={styles.cardHeaderLeft}>
                <span className={styles.cardHeaderIcon}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
                  </svg>
                </span>
                <h4 className={styles.cardHeaderTitle}>Property Information</h4>
              </div>
            </div>

            <div className={styles.infoGrid}>
              <div className={styles.infoRow}>
                <span className={styles.infoKey}>Property ID:</span>
                <span className={styles.infoVal}>{propId}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoKey}>Address:</span>
                <span className={styles.infoVal}>{propAddress}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoKey}>City:</span>
                <span className={styles.infoVal}>{propCity}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoKey}>State:</span>
                <span className={styles.infoVal}>{propState}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoKey}>ZIP Code:</span>
                <span className={styles.infoVal}>{propZip}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoKey}>Property Added:</span>
                <span className={styles.infoVal}>{propAdded}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoKey}>Property Type:</span>
                <span className={styles.infoVal}>{propType}</span>
              </div>
            </div>
          </div>

          {/* Card 3: Documents Accordion */}
          <div className={styles.detailCard}>
            <div
              className={styles.cardHeaderRow}
              onClick={() => setIsDocumentsOpen((prev) => !prev)}
              role="button"
              tabIndex={0}
              aria-expanded={isDocumentsOpen}
            >
              <div className={styles.cardHeaderLeft}>
                <span className={styles.cardHeaderIcon}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z" />
                  </svg>
                </span>
                <h4 className={styles.cardHeaderTitle}>Documents</h4>
              </div>
              <button
                type="button"
                className={`${styles.accordionChevron} ${!isDocumentsOpen ? styles.collapsed : ""}`}
                aria-label={isDocumentsOpen ? "Collapse documents" : "Expand documents"}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="18 15 12 9 6 15"></polyline>
                </svg>
              </button>
            </div>

            {isDocumentsOpen && (
              <div className={styles.documentsList}>
                {mainDocuments.map((doc, idx) => (
                  <div
                    key={`${doc.title}-${idx}`}
                    className={styles.documentItem}
                    onClick={() => handleOpenDocument(doc)}
                    role="button"
                    tabIndex={0}
                  >
                    <div className={styles.docItemLeft}>
                      <span className={styles.docIcon}>
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                          <polyline points="14 2 14 8 20 8"></polyline>
                          <line x1="16" y1="13" x2="8" y2="13"></line>
                          <line x1="16" y1="17" x2="8" y2="17"></line>
                          <polyline points="10 9 9 9 8 9"></polyline>
                        </svg>
                      </span>
                      <span className={styles.docTitle}>{doc.title}</span>
                    </div>
                    <span className={styles.docActionIcon}>
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                        <polyline points="15 3 21 3 21 9"></polyline>
                        <line x1="10" y1="14" x2="21" y2="3"></line>
                      </svg>
                    </span>
                  </div>
                ))}

                {/* Subheading for Supporting Documents */}
                <h5 className={styles.supportingDocsHeader}>Supporting Documents</h5>

                <div className={styles.supportingDocsGrid}>
                  {supportingDocsList.map((sDoc) => (
                    <div
                      key={sDoc.id}
                      className={styles.supportingDocCard}
                      onClick={() => handleOpenDocument(sDoc)}
                      role="button"
                      tabIndex={0}
                    >
                      <div className={styles.supportingDocLeft}>
                        {sDoc.thumbnailUrl ? (
                          <img
                            src={sDoc.thumbnailUrl}
                            alt={sDoc.title}
                            className={styles.supportingDocThumbnail}
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                              const fallback = e.currentTarget.parentElement?.querySelector(
                                `.${styles.supportingDocFallback}`
                              ) as HTMLElement | null;
                              if (fallback) fallback.style.display = "flex";
                            }}
                          />
                        ) : null}
                        <div
                          className={styles.supportingDocFallback}
                          style={sDoc.thumbnailUrl ? { display: "none" } : undefined}
                        >
                          <svg
                            width="22"
                            height="22"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                            <circle cx="8.5" cy="8.5" r="1.5"></circle>
                            <polyline points="21 15 16 10 5 21"></polyline>
                          </svg>
                        </div>
                        <span className={styles.supportingDocName}>{sDoc.title}</span>
                      </div>
                      <span className={styles.docActionIcon}>
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                          <polyline points="15 3 21 3 21 9"></polyline>
                          <line x1="10" y1="14" x2="21" y2="3"></line>
                        </svg>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Card 4: Timeline Accordion */}
          <div className={styles.detailCard}>
            <div
              className={styles.cardHeaderRow}
              onClick={() => setIsTimelineOpen((prev) => !prev)}
              role="button"
              tabIndex={0}
              aria-expanded={isTimelineOpen}
            >
              <div className={styles.cardHeaderLeft}>
                <span className={styles.cardHeaderIcon}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z" />
                  </svg>
                </span>
                <h4 className={styles.cardHeaderTitle}>Timeline</h4>
              </div>
              <button
                type="button"
                className={`${styles.accordionChevron} ${!isTimelineOpen ? styles.collapsed : ""}`}
                aria-label={isTimelineOpen ? "Collapse timeline" : "Expand timeline"}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="18 15 12 9 6 15"></polyline>
                </svg>
              </button>
            </div>

            {isTimelineOpen && (
              <div className={styles.timelineContainer}>
                <div className={styles.timelineItem}>
                  <div className={styles.timelineNode}>
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                      <line x1="16" y1="13" x2="8" y2="13"></line>
                      <line x1="16" y1="17" x2="8" y2="17"></line>
                    </svg>
                  </div>
                  <div className={styles.timelineDetails}>
                    <h5 className={styles.timelineMilestoneTitle}>Protest Submitted</h5>
                    <p className={styles.timelineMilestoneDesc}>
                      Your protest was successfully submitted for review
                    </p>
                    <span className={styles.timelineMilestoneDate}>{timelineDate}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Card 5: Contact Information */}
          <div className={styles.detailCard}>
            <div className={`${styles.cardHeaderRow} ${styles.cardHeaderRowNonClickable}`}>
              <div className={styles.cardHeaderLeft}>
                <span className={styles.cardHeaderIcon}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                  </svg>
                </span>
                <h4 className={styles.cardHeaderTitle}>Contact Information</h4>
              </div>
            </div>

            <div className={styles.contactGrid}>
              <div className={styles.contactRow}>
                <span className={styles.contactKey}>Name:</span>
                <span className={styles.contactVal}>{contactName}</span>
              </div>
              <div className={styles.contactRow}>
                <span className={styles.contactKey}>Email:</span>
                <span className={styles.contactVal}>{contactEmail}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* In-App Document & Image Preview Modal */}
      <DocumentPreviewModal
        isOpen={Boolean(previewDoc)}
        onClose={() => setPreviewDoc(null)}
        title={previewDoc?.title || "Document Preview"}
        url={previewDoc?.url || null}
      />
    </div>
  );
};
